import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import React, { useMemo } from "react";
import PropTypes from "prop-types";

const mailBar =
  "flex h-11 items-center justify-between gap-2 rounded-xl border border-wa-border bg-wa-panel px-2 py-1.5";
const mailSelect =
  "h-9 cursor-pointer rounded-lg border border-wa-border bg-wa-panel2 py-1 pl-2 pr-7 text-xs font-medium text-wa-text outline-none focus:border-wa-accent focus:ring-1 focus:ring-wa-accent";
const mailBtn =
  "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text disabled:pointer-events-none disabled:opacity-30";

const Pagination = React.memo(
  ({
    setPageSize,
    setPage,
    page,
    pageSize,
    pageRanges = [10, 25, 50, 100, 150, 200],
    variant,
  }) => {
    const previous = () => setPage((prev) => (prev <= 0 ? 0 : prev - 1));
    const next = () => setPage((prev) => prev + 1);

    const pageRange = useMemo(() => {
      const start = page * Number(pageSize) + 1;
      const end = page * Number(pageSize) + Number(pageSize);
      return { start, end };
    }, [page, pageSize]);

    const pageSizeOptions = useMemo(() => pageRanges, [pageRanges]);

    const handlePageSizeChange = (e) => {
      const v = Number(e.target.value);
      setPageSize(v);
      setPage(0);
    };

    const isCompactBar = variant === "mail" || variant === "compact";

    if (isCompactBar) {
      const selectId = variant === "mail" ? "pagesize-mail" : "pagesize-compact";
      return (
        <div className={mailBar} role="navigation" aria-label="Lijst paginering">
          <label className="flex min-w-0 flex-1 items-center gap-2">
            <span className="hidden shrink-0 text-xs text-wa-text/70 sm:inline">Per pagina</span>
            <select
              id={selectId}
              name="pagesize"
              value={Number(pageSize)}
              onChange={handlePageSizeChange}
              className={mailSelect}
              aria-label="Aantal per pagina"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={previous}
              disabled={page <= 0}
              className={mailBtn}
              aria-label="Vorige pagina"
            >
              <ChevronLeftIcon className="h-5 w-5" aria-hidden />
            </button>
            <span
              className="min-w-[3.75rem] select-none text-center text-xs font-medium tabular-nums text-wa-text"
              aria-live="polite"
            >
              {pageRange.start}–{pageRange.end}
            </span>
            <button type="button" onClick={next} className={mailBtn} aria-label="Volgende pagina">
              <ChevronRightIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="my-4 ml-4 flex sm:flex-none">
        <select
          id="pagesize"
          name="pagesize"
          value={Number(pageSize)}
          onChange={handlePageSizeChange}
          className="rounded-md border border-wa-border bg-wa-panel2 p-2 text-wa-text outline-none focus:border-wa-accent focus:ring-1 focus:ring-wa-accent"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={previous}
          className="border border-wa-border bg-wa-panel px-4 text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text"
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
        <button
          type="button"
          className="rounded-l border border-wa-border bg-wa-panel px-4 text-wa-text"
          tabIndex={-1}
          aria-hidden
        >
          {pageRange.start} - {pageRange.end}
        </button>
        <button
          type="button"
          onClick={next}
          className="border border-wa-border bg-wa-panel px-4 text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text"
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>
      </div>
    );
  }
);

Pagination.displayName = "Pagination";

Pagination.propTypes = {
  setPageSize: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  pageRanges: PropTypes.arrayOf(PropTypes.number),
  variant: PropTypes.oneOf(["mail", "compact"]),
};

export default Pagination;
